import {
  CircleGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  PlaneGeometry,
  RingGeometry,
} from "three";
import { type Aircraft, bodyLength, cruiseSpeed } from "../aircraft/Aircraft";
import { AIRCRAFT_SPECS, type AircraftKind } from "../aircraft/AircraftCatalog";
import { isOutbound } from "../aircraft/AircraftState";
import { AIRFRAMES, type Airframe } from "../aircraft/Airframes";
import { FEEDBACK } from "../config/gameplay";
import { LAYER, PALETTE, mixColor } from "../config/palette";
import {
  RUNWAY_SYMBOLS,
  SYMBOL_BADGE_COLOR,
  SYMBOL_SIZING,
} from "../config/symbols";
import {
  clamp,
  shortestAngleDelta,
  signOf,
  smoothStep,
} from "../simulation/geometry";
import { AircraftModelLibrary } from "./AircraftSilhouettes";
import type { AircraftTemplate } from "./AssetLoader";
import { planeSilhouetteGeometry, regularPolygonGeometry } from "./geometry";

/** Model scale factor: spec length is multiplied by this to get world units. */
export const AIRCRAFT_SCALE = 1.7;

const BANK_LIMIT = 0.92;
const BANK_DEADZONE = 0.045;
const BANK_STIFFNESS = 0.11;
const BANK_DAMPING = 0.76;
const HEADING_SMOOTHING = 0.34;

/** Gentle idle bob, de-synchronised per aircraft and speed. */
function wobbleAt(id: number, time: number, speed: number): number {
  return (
    Math.sin(time * (7.2 + speed * 0.16) + id * 1.73) * 0.16 +
    Math.sin(time * (11.5 + speed * 0.09) + id * 0.61) * 0.055
  );
}

export interface AircraftEntry {
  readonly group: Group;
  readonly flightRig: Group;
  readonly bodyMaterials: MeshLambertMaterial[];
  readonly bodyMaterial: MeshLambertMaterial;
  readonly darkMaterial: MeshLambertMaterial;
  readonly halo: Mesh;
  readonly shadow: Mesh;
  /** Colour-blind badge: a disc with the kind's shape on it. */
  readonly badge: Group;
  /** Speed lines off the wingtips, shown while a departure accelerates. */
  readonly streaks: Group;
  readonly streakMaterial: MeshBasicMaterial;
  /** Anti-collision light, blinking while the aircraft sits on the apron. */
  readonly beacon: Mesh;
  visualHeading: number;
  selectionMix: number;
  bank: number;
  bankVelocity: number;
}

export interface AircraftViewFactoryOptions {
  /** The imported toy plane, if it loaded; the rest of the fleet is generated. */
  readonly toy?: AircraftTemplate;
}

/**
 * Creates and animates the aircraft models.
 *
 * Each aircraft is a three level rig: an outer group holding world position
 * and heading, a flight rig that banks/climbs, and an asset correction group
 * that fixes the model's own axes. Which airframe it wears comes from
 * `AircraftModelLibrary`, keyed off colour and id.
 */
export class AircraftViewFactory {
  private readonly models: AircraftModelLibrary;
  readonly haloGeometry = new RingGeometry(1.02, 1.08, 32);
  readonly warningRingGeometry = new RingGeometry(0.8, 1.08, 28);
  private readonly shadowGeometry = planeSilhouetteGeometry();
  /** One unit long, so an entry scales it to the streak length it wants. */
  private readonly streakGeometry = new PlaneGeometry(1, 1);
  // The beacon is the same dot on every aircraft, so geometry and material are
  // shared: the blink is a visibility toggle, which needs neither.
  private readonly beaconGeometry = new CircleGeometry(0.42, 8);
  private readonly beaconMaterial = new MeshBasicMaterial({
    color: PALETTE.beacon,
    transparent: true,
    opacity: 0.92,
    depthTest: false,
    depthWrite: false,
  });

  // The colour-blind badge is identical for every aircraft of a kind, so its
  // geometry and materials are shared and disposed with the factory, not with
  // the individual entries.
  private readonly badgeDiscGeometry = new CircleGeometry(1, 16);
  private readonly badgeSymbolGeometries = new Map<
    AircraftKind,
    ReturnType<typeof regularPolygonGeometry>
  >();
  private readonly badgeDiscMaterial = new MeshBasicMaterial({
    color: SYMBOL_BADGE_COLOR,
    transparent: true,
    opacity: 0.94,
    depthTest: false,
    depthWrite: false,
  });
  private readonly badgeSymbolMaterial = new MeshBasicMaterial({
    color: PALETTE.aircraftDark,
    depthTest: false,
    depthWrite: false,
  });
  private symbolsVisible = false;

  constructor(options: AircraftViewFactoryOptions = {}) {
    this.models = new AircraftModelLibrary(options.toy);
  }

  /** Colour-blind mode: shows the shape badge over every aircraft. */
  setSymbolsVisible(visible: boolean): void {
    this.symbolsVisible = visible;
  }

  private badgeSymbolGeometry(
    kind: AircraftKind,
  ): ReturnType<typeof regularPolygonGeometry> {
    const cached = this.badgeSymbolGeometries.get(kind);
    if (cached !== undefined) {
      return cached;
    }
    const symbol = RUNWAY_SYMBOLS[kind];
    const geometry = regularPolygonGeometry(
      symbol.sides,
      symbol.rotation,
      0.62,
    );
    this.badgeSymbolGeometries.set(kind, geometry);
    return geometry;
  }

  private createBadge(kind: AircraftKind): Group {
    const badge = new Group();
    badge.name = "aircraft-symbol-badge";

    const disc = new Mesh(this.badgeDiscGeometry, this.badgeDiscMaterial);
    disc.renderOrder = 15;
    badge.add(disc);

    const shape = this.badgeSymbolGeometry(kind);
    if (shape) {
      const glyph = new Mesh(shape, this.badgeSymbolMaterial);
      glyph.position.z = 0.01;
      glyph.renderOrder = 16;
      badge.add(glyph);
    }

    badge.scale.setScalar(SYMBOL_SIZING.aircraftBadge);
    badge.position.z = SYMBOL_SIZING.aircraftBadgeHeight;
    badge.visible = this.symbolsVisible;
    return badge;
  }

  create(
    kind: AircraftKind,
    airframe: Airframe = AIRFRAMES.airliner,
  ): AircraftEntry {
    const spec = AIRCRAFT_SPECS[kind];
    const length = spec.length * airframe.lengthFactor;
    const template = this.models.templateFor(airframe.key);
    const group = new Group();
    const flightRig = new Group();
    const correction = new Group();
    correction.name = "aircraft-asset-correction";

    const model = template.object.clone(true);
    const bodyMaterial = new MeshLambertMaterial({
      color: spec.color,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: 2,
    });
    const darkMaterial = new MeshLambertMaterial({
      color: PALETTE.aircraftDark,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: 2,
    });

    model.traverse((child) => {
      if (!(child instanceof Mesh)) {
        return;
      }
      const source = Array.isArray(child.material)
        ? child.material[0]
        : child.material;
      child.material =
        (source as { name?: string } | undefined)?.name === "Material_3"
          ? darkMaterial
          : bodyMaterial;
      child.renderOrder = 10;
    });

    if (template.basis) {
      correction.setRotationFromMatrix(template.basis);
    }
    correction.scale.setScalar((length * AIRCRAFT_SCALE) / template.length);
    correction.add(model);
    flightRig.add(correction);

    const halo = new Mesh(
      this.haloGeometry,
      new MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.78,
        depthTest: false,
        depthWrite: false,
      }),
    );
    halo.name = "aircraft-airborne-halo";
    halo.scale.setScalar(spec.radius * AIRCRAFT_SCALE * 2.85);
    halo.position.z = -LAYER.aircraft + LAYER.halo + 0.01;
    halo.renderOrder = 13;

    const shadow = new Mesh(
      this.shadowGeometry,
      new MeshBasicMaterial({
        color: PALETTE.aircraftShadow,
        transparent: true,
        opacity: 0.18,
        depthWrite: false,
      }),
    );
    shadow.name = "aircraft-shadow";
    shadow.scale.set(
      length * AIRCRAFT_SCALE * 0.52,
      spec.radius * AIRCRAFT_SCALE * 0.9,
      1,
    );
    shadow.position.z = -LAYER.aircraft + 0.15;

    const badge = this.createBadge(kind);

    // Two quads trailing the wingtips. They ride on the flight rig, so they
    // bank and climb with the aircraft instead of staying flat on the ground.
    const streakMaterial = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
    });
    const streaks = new Group();
    streaks.name = "aircraft-streaks";
    streaks.visible = false;
    for (const side of [-1, 1]) {
      const streak = new Mesh(this.streakGeometry, streakMaterial);
      streak.renderOrder = 11;
      streak.position.y = side * spec.radius * AIRCRAFT_SCALE * 0.62;
      streaks.add(streak);
    }
    flightRig.add(streaks);

    const beacon = new Mesh(this.beaconGeometry, this.beaconMaterial);
    beacon.name = "aircraft-beacon";
    beacon.renderOrder = 14;
    // Above the fuselage, a touch aft: the light, not a headlamp.
    beacon.position.set(-length * 0.18, 0, 1.4);
    beacon.visible = false;

    group.add(shadow, flightRig, halo, badge, beacon);

    return {
      group,
      flightRig,
      bodyMaterials: [bodyMaterial, darkMaterial],
      bodyMaterial,
      darkMaterial,
      halo,
      shadow,
      badge,
      streaks,
      streakMaterial,
      beacon,
      visualHeading: 0,
      selectionMix: 0,
      bank: 0,
      bankVelocity: 0,
    };
  }

  /** Drive one aircraft's visuals from its simulation state. */
  update(
    entry: AircraftEntry,
    aircraft: Aircraft,
    time: number,
    selected: boolean,
    warned: boolean,
    /** 0 at the edge of the warning radius, 1 at contact. */
    warningLevel = 0,
  ): void {
    const outbound = isOutbound(aircraft.state);
    entry.bodyMaterial.color.setHex(
      outbound ? PALETTE.outboundAircraft : aircraft.spec.color,
    );
    entry.darkMaterial.color.setHex(
      outbound ? PALETTE.outboundAircraftDark : PALETTE.aircraftDark,
    );
    entry.group.position.set(aircraft.pos.x, aircraft.pos.y, LAYER.aircraft);

    const turn = shortestAngleDelta(entry.visualHeading, aircraft.heading);
    entry.visualHeading += turn * HEADING_SMOOTHING;
    entry.group.rotation.z = entry.visualHeading;

    const taxiing =
      aircraft.state === "taxiing-in" ||
      aircraft.state === "taxiing-out" ||
      aircraft.state === "holding";
    const departing = aircraft.state === "departing";

    const bankTarget =
      taxiing ||
      (departing && aircraft.takeoffProgress < 0.72) ||
      Math.abs(turn) < BANK_DEADZONE
        ? 0
        : clamp(
            -signOf(turn) * (0.22 + Math.abs(turn) * 2.6),
            -BANK_LIMIT,
            BANK_LIMIT,
          );
    entry.bankVelocity =
      (entry.bankVelocity + (bankTarget - entry.bank) * BANK_STIFFNESS) *
      BANK_DAMPING;
    entry.bank = clamp(
      entry.bank + entry.bankVelocity,
      -BANK_LIMIT,
      BANK_LIMIT,
    );

    const landing = aircraft.state === "landing" ? aircraft.landProgress : 0;
    const climb = departing
      ? smoothStep(aircraft.takeoffProgress, 0.42, 0.86)
      : 0;
    const grounded = taxiing
      ? 1
      : aircraft.state === "landing"
        ? landing
        : departing
          ? 1 - climb
          : 0;
    const airborne = 1 - grounded;
    const wobble =
      wobbleAt(aircraft.id, time, cruiseSpeed(aircraft)) * airborne;
    const scale = 1 - grounded * 0.46;

    entry.flightRig.scale.setScalar(scale);
    // An aircraft told to hold rocks against the brakes instead of sitting
    // dead: the queue is the one place on the board where nothing else moves.
    // Local +x is forward, because the outer group carries the heading.
    entry.flightRig.position.x =
      aircraft.state === "holding"
        ? Math.sin(time * 1.65 + aircraft.id * 2.1) * FEEDBACK.holdingSway
        : 0;
    entry.flightRig.position.z = wobble - grounded * 1.05;
    entry.flightRig.rotation.x = entry.bank + wobble * 0.16;
    entry.flightRig.rotation.y = taxiing
      ? 0
      : landing * -0.34 + (departing ? climb * 0.28 : 0) + wobble * 0.035;

    entry.shadow.scale.set(
      bodyLength(aircraft) * AIRCRAFT_SCALE * scale * 0.52,
      aircraft.spec.radius *
        AIRCRAFT_SCALE *
        scale *
        (0.9 - Math.abs(entry.bank) * 0.2),
      1,
    );

    // The shadow slides away from the aircraft as it climbs.
    const drift =
      grounded > 0.98 ? 0.16 : airborne * (1.25 + Math.max(0, wobble) * 0.12);
    const along = drift * 0.8;
    const across = drift * 0.6;
    const cos = Math.cos(entry.visualHeading);
    const sin = Math.sin(entry.visualHeading);
    entry.shadow.position.x = cos * along + sin * across;
    entry.shadow.position.y = -sin * along + cos * across;
    (entry.shadow.material as MeshBasicMaterial).opacity =
      grounded > 0.98
        ? 0.05
        : 0.18 * (1 - grounded * 0.44 - Math.max(0, wobble) * 0.12);

    const bodyOpacity =
      aircraft.state === "landing" ? 1 - landing * 0.18 : taxiing ? 0.82 : 1;
    for (const material of entry.bodyMaterials) {
      material.opacity = bodyOpacity;
    }

    entry.selectionMix += ((selected ? 1 : 0) - entry.selectionMix) * 0.22;
    if (Math.abs(entry.selectionMix - (selected ? 1 : 0)) < 0.002) {
      entry.selectionMix = selected ? 1 : 0;
    }

    entry.halo.geometry = warned ? this.warningRingGeometry : this.haloGeometry;
    entry.halo.visible =
      aircraft.state === "flying" || (departing && climb > 0.35);

    // The warning ring closes in as the gap does, and beats faster with it, so
    // "how close" is readable without counting pixels between two aircraft.
    const closeness = warned ? clamp(warningLevel, 0, 1) : 0;
    const ringScale = warned
      ? 1 - closeness * (1 - FEEDBACK.warningRingTightest)
      : 1;
    const baseRadius = aircraft.spec.radius * AIRCRAFT_SCALE * 2.85;
    entry.halo.scale.setScalar(baseRadius * ringScale);

    const haloMaterial = entry.halo.material as MeshBasicMaterial;
    haloMaterial.color.setHex(
      warned
        ? mixColor(PALETTE.warning, PALETTE.crash, closeness * 0.55)
        : mixColor(0xffffff, aircraft.spec.color, entry.selectionMix),
    );
    haloMaterial.opacity = warned
      ? 0.7 +
        closeness * 0.18 +
        Math.sin(time * (8.333 + closeness * 9)) * (0.1 + closeness * 0.06)
      : departing
        ? 0.5
        : 0.78 + entry.selectionMix * 0.12;

    // Speed lines: only once the roll is committed, and they stretch as the
    // aircraft accelerates rather than appearing at full length.
    const rolling = departing
      ? smoothStep(aircraft.takeoffProgress, FEEDBACK.streakFromProgress, 1)
      : 0;
    entry.streaks.visible = rolling > 0.01;
    if (entry.streaks.visible) {
      const length = FEEDBACK.streakLength * (0.4 + rolling * 0.6);
      const flicker = 0.82 + Math.sin(time * 26 + aircraft.id) * 0.18;
      entry.streakMaterial.opacity = 0.5 * rolling * flicker;
      for (const streak of entry.streaks.children) {
        streak.scale.set(length, 0.22, 1);
        // Behind the tail, growing backwards from it.
        streak.position.x = -bodyLength(aircraft) * 0.5 - length / 2;
      }
    }

    entry.shadow.visible = aircraft.state !== "parked";

    // Parked on the apron: the beacon is the only sign it is still a live
    // aircraft rather than scenery. A duty cycle, not a fade — a pixel light
    // is on or off, and the phase is offset per id so a row does not blink in
    // unison.
    entry.beacon.visible =
      aircraft.state === "parked" &&
      Math.sin(time * FEEDBACK.beaconRate + aircraft.id * 1.9) >
        FEEDBACK.beaconDuty;

    // The badge hangs above the aircraft and counter-rotates, so the shape
    // keeps one orientation however the aircraft is heading.
    entry.badge.visible = this.symbolsVisible && aircraft.state !== "parked";
    entry.badge.rotation.z = -entry.visualHeading;
    entry.badge.position.z =
      SYMBOL_SIZING.aircraftBadgeHeight - grounded * 1.05 + wobble * 0.5;
  }

  disposeEntry(entry: AircraftEntry): void {
    for (const material of entry.bodyMaterials) {
      material.dispose();
    }
    (entry.halo.material as MeshBasicMaterial).dispose();
    (entry.shadow.material as MeshBasicMaterial).dispose();
    entry.streakMaterial.dispose();
  }

  dispose(): void {
    this.haloGeometry.dispose();
    this.warningRingGeometry.dispose();
    this.shadowGeometry.dispose();
    this.streakGeometry.dispose();
    this.beaconGeometry.dispose();
    this.beaconMaterial.dispose();
    this.badgeDiscGeometry.dispose();
    for (const geometry of this.badgeSymbolGeometries.values()) {
      geometry?.dispose();
    }
    this.badgeSymbolGeometries.clear();
    this.badgeDiscMaterial.dispose();
    this.badgeSymbolMaterial.dispose();
    this.models.dispose();
  }
}
